import React, {useEffect, useState, useCallback} from "react";
import { useQuery, useQueryClient } from 'react-query';
import Recipe from '../Recipe';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import AdComponent from "./AdComponent";
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_RECIPE_QUERY, useRecipeSearch } from '../contexts/RecipeSearchContext';
import { supabase } from '../lib/supabase';
import { Search } from 'lucide-react';

const FREE_RECIPE_RESULTS = 5
const PREMIUM_RECIPE_RESULTS = 15
const MAX_SAVED_RECIPES = 35

const getFunctionErrorMessage = async (error, fallbackMessage) => {
  if (error?.context?.json) {
    try {
      const body = await error.context.json()
      if (body?.error) return body.error
    } catch {
      // Fall back to the default message below.
    }
  }

  return error?.message || fallbackMessage
}

/**
 * Main Application Component
 * Contains the recipe search functionality - only accessible to authenticated users
 * This was extracted from App.js to create separation between landing and main app
 * Now includes tier-based search limits and ad display logic
 */
const MainApp = () => {
  const { user, userProfile, incrementSearchCount } = useAuth()
  const { query, setQuery } = useRecipeSearch()
  const queryClient = useQueryClient()

  // STATE MANAGEMENT: Using React's useState hook for component state
  const [search, setSearch] = useState("");
  const [savedRecipeUrls, setSavedRecipeUrls] = useState(new Set());
  const [savedRecipeCount, setSavedRecipeCount] = useState(0);
  const [savingRecipeUrls, setSavingRecipeUrls] = useState(new Set());
  const [saveLimitMessage, setSaveLimitMessage] = useState('');
  const isPremium = userProfile?.tier === 'premium';
  const recipeResultLimit = isPremium ? PREMIUM_RECIPE_RESULTS : FREE_RECIPE_RESULTS;
  const normalizedQuery = query.trim() || DEFAULT_RECIPE_QUERY;

  const canSearchRecipes = Boolean(user?.id && userProfile?.tier)
  const {
    data: recipes = [],
    isFetching,
    isLoading,
    isError,
    error: recipesError
  } = useQuery(
    ['recipes', user?.id, normalizedQuery, recipeResultLimit],
    async () => {
      const { data, error } = await supabase.functions.invoke('search-recipes', {
        body: {
          query: normalizedQuery,
          limit: recipeResultLimit
        }
      })

      if (error) {
        throw new Error(await getFunctionErrorMessage(error, 'Unable to fetch recipes right now.'))
      }

      if (data?.error) {
        throw new Error(data.error)
      }

      const hits = Array.isArray(data?.hits) ? data.hits : []
      console.log('Recipes found:', hits.length)
      return hits.slice(0, recipeResultLimit)
    },
    {
      enabled: canSearchRecipes,
      retry: 1,
      onError: (error) => {
        console.error('Error fetching recipes:', error)
      }
    }
  );
  // Avoid flashing the empty state before profile is ready or while a search is in flight.
  const loading = !canSearchRecipes || isLoading || isFetching;

  // EVENT HANDLERS: Functions that respond to user interactions
  const updateSearch = e => {
    setSearch(e.target.value);
    console.log(search);
  }

  const submitSearchQuery = useCallback(async (nextQuery) => {
    const submittedQuery = nextQuery.trim()

    if (!submittedQuery) {
      return
    }

    if (submittedQuery === normalizedQuery) {
      setSearch('')
      return
    }
    
    // Note: Search limits temporarily removed - free tier gets unlimited searches
    // Keeping increment for analytics/future use (but not enforcing limits)
    if (userProfile?.tier === 'free') {
      const result = await incrementSearchCount()
      if (result.error) {
        console.error('Error updating search count:', result.error)
      }
    }
    
    setQuery(submittedQuery);
    setSearch('');
  }, [incrementSearchCount, normalizedQuery, setQuery, userProfile?.tier])

  const getSearch = async (e) => {
    e.preventDefault();
    await submitSearchQuery(search);
  }

  useEffect(() => {
    if (!isPremium || !user?.id) {
      setSavedRecipeUrls(new Set());
      setSavedRecipeCount(0);
      return;
    }

    let isCurrent = true;

    const getSavedRecipeUrls = async () => {
      const { data, error } = await supabase
        .from('saved_recipes')
        .select('recipe_url')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (!isCurrent) return;

      if (error) {
        console.error('Error fetching saved recipes:', error);
        setSavedRecipeUrls(new Set());
        setSavedRecipeCount(0);
        return;
      }
      const savedUrls = (data || []).map(savedRecipe => savedRecipe.recipe_url).filter(Boolean);
      setSavedRecipeUrls(new Set(savedUrls));
      setSavedRecipeCount(savedUrls.length);
    };

    getSavedRecipeUrls();

    return () => {
      isCurrent = false;
    };
  }, [isPremium, user?.id]);

  const handleToggleSavedRecipe = useCallback(async (recipe) => {
    if (!isPremium || !user?.id || !recipe?.url) return;

    const recipeUrl = recipe.url;
    const isAlreadySaved = savedRecipeUrls.has(recipeUrl);

    setSaveLimitMessage('');

    if (!isAlreadySaved && savedRecipeCount >= MAX_SAVED_RECIPES) {
      setSaveLimitMessage(`You have saved ${MAX_SAVED_RECIPES}/${MAX_SAVED_RECIPES} recipes. Delete one saved recipe before saving another.`);
      return;
    }

    setSavingRecipeUrls(previous => {
      const next = new Set(previous);
      next.add(recipeUrl);
      return next;
    });

    try {
      if (isAlreadySaved) {
        const { error } = await supabase.functions.invoke('delete-saved-recipe', {
          body: {
            recipe_url: recipeUrl
          }
        });

        if (error) {
          throw new Error(await getFunctionErrorMessage(error, 'Unable to remove saved recipe. Please try again.'));
        }

        setSavedRecipeUrls(previous => {
          const next = new Set(previous);
          next.delete(recipeUrl);
          return next;
        });
        setSavedRecipeCount(previous => Math.max(0, previous - 1));
      } else {
        const { data, error } = await supabase.functions.invoke('save-recipe', {
          body: {
            title: recipe.label,
            calories: Math.round(recipe.calories || 0),
            ingredients: (recipe.ingredients || []).map(ingredient => ingredient.text).filter(Boolean),
            recipe_url: recipeUrl,
            source_image_url: recipe.image
          }
        });

        if (error) {
          throw new Error(await getFunctionErrorMessage(error, 'Unable to save recipe. Please try again.'));
        }
        const wasInserted = data?.saved !== false;

        setSavedRecipeUrls(previous => {
          const next = new Set(previous);
          next.add(recipeUrl);
          return next;
        });
        if (wasInserted) {
          setSavedRecipeCount(previous => Math.min(MAX_SAVED_RECIPES, previous + 1));
        }
      }
    } catch (error) {
      console.error('Error updating saved recipe:', error);
      const message = error?.message?.includes('Saved recipe limit reached')
        ? `You have saved ${MAX_SAVED_RECIPES}/${MAX_SAVED_RECIPES} recipes. Delete one saved recipe before saving another.`
        : 'Unable to update saved recipe. Please try again.';
      setSaveLimitMessage(message);
      alert(message);
    } finally {
      setSavingRecipeUrls(previous => {
        const next = new Set(previous);
        next.delete(recipeUrl);
        return next;
      });
    }
  }, [isPremium, savedRecipeCount, savedRecipeUrls, user?.id]);

  return (
    <>
      {/* MAIN APPLICATION CONTENT */}
      <div className="recipe-app-shell min-h-screen bg-recipe-gradient">
        
        {/* Enhanced Hero Section */}
        <div className="text-center pt-8 pb-6 px-4">
          <h2 className="text-subtitle lg:text-title font-bold text-white mb-4 animate-fade-in">
            "From quick bites to gourmet delights - it's all here."
          </h2>

        </div>

        {/* ENHANCED SEARCH FORM: Glass morphism design */}
        <div className="flex min-h-[12vh] items-center justify-center px-3 pb-6 sm:px-4">
          <div className="w-full max-w-4xl">
            <form onSubmit={getSearch} className="relative">
              {/* Glass morphism container */}
              <div className="animate-slide-up rounded-2xl border border-white/30 bg-glass-light p-3 shadow-glass backdrop-blur-lg sm:rounded-3xl sm:p-6">
                <p className="mb-2.5 pl-1 text-left text-xs font-medium text-white/90 animate-fade-in sm:mb-4 sm:pl-2 sm:text-base md:pl-4 md:text-lg">
                  Find recipes by ingredient, dish, or even drinks
                </p>
                <div className="flex flex-row items-center gap-2 sm:gap-4">

                  {/* Enhanced Search Input */}
                  <div className="relative min-w-0 flex-1">
                    <div className="pointer-events-none absolute inset-y-0 left-4 hidden items-center sm:flex">
                      <span className="text-2xl">🔍</span>
                    </div>
                    <Input
                      className="recipe-search-input h-11 rounded-xl border-white/40 bg-white/90 px-3 text-sm shadow-modern backdrop-blur-sm transition-all duration-300 placeholder:text-xs placeholder:text-gray-500 placeholder:font-medium focus:bg-white focus:shadow-glow sm:h-14 sm:rounded-2xl sm:pl-14 sm:pr-6 sm:text-lg sm:placeholder:text-base"
                      type="text"
                      placeholder="What would you like to cook or drink today?"
                      value={search}
                      onChange={updateSearch}
                    />
                  </div>

                  {/* Enhanced Search Button */}
                  <Button
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="h-11 shrink-0 rounded-xl border-0 bg-gradient-to-r from-orange-500 to-orange-600 px-3 text-sm font-bold text-white shadow-modern transition-all duration-300 hover:-translate-y-1 hover:from-orange-600 hover:to-orange-700 hover:text-white hover:shadow-glow sm:h-14 sm:rounded-2xl sm:px-8 sm:text-base whitespace-nowrap"
                  >
                    {loading ? (
                      <span className="flex items-center gap-1.5 sm:gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white sm:h-5 sm:w-5"></div>
                        <span className="hidden xs:inline">Searching...</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 sm:gap-2">
                        Search
                        <Search className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" strokeWidth={2.5} />
                      </span>
                    )}
                  </Button>
                </div>

                {/* Search suggestions hint */}
                <div className="mt-2.5 flex flex-nowrap items-center justify-start gap-1.5 overflow-x-auto pb-0.5 text-xs sm:mt-4 sm:flex-wrap sm:justify-center sm:gap-2.5 sm:overflow-visible sm:pb-0 sm:text-base md:gap-3 md:text-lg">
                  <span className="shrink-0 font-medium text-white/80">Try:</span>
                  {['pasta', 'chicken', 'salad', 'dessert', 'vegetarian'].map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => submitSearchQuery(suggestion)}
                      className="shrink-0 rounded-full bg-white/20 px-2.5 py-1 text-xs font-medium text-white transition-all duration-300 hover:scale-105 hover:bg-white/30 sm:px-4 sm:py-1.5 sm:text-base md:px-5 md:py-2 md:text-lg"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* AD PLACEMENT: Below search form, before results (FREE TIER ONLY) */}
        {!loading && recipes.length > 0 && userProfile?.tier === 'free' && (
          <div className="px-4">
            <AdComponent 
              placement="below-search" 
              size="medium"
              className="mb-4"
            />
          </div>
        )}

        {isPremium && saveLimitMessage && (
          <div className="mx-auto max-w-2xl px-4">
            <div className="rounded-2xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-center text-sm font-medium text-yellow-800 shadow-modern">
              {saveLimitMessage}
            </div>
          </div>
        )}

        {/* RECIPE DISPLAY SECTION - Original Layout */}
        <div className="flex flex-wrap items-stretch justify-around px-4 py-6 gap-4 min-h-[400px]">
          
          {/* ENHANCED LOADING STATE */}
          {loading && (
            <div className="w-full text-center py-12 animate-fade-in">
              <div className="bg-glass-warm backdrop-blur-lg rounded-3xl p-8 border border-white/30 shadow-glass inline-block max-w-md">
                <div className="w-16 h-16 mx-auto mb-4 bg-orange-500 rounded-2xl flex items-center justify-center text-2xl animate-pulse shadow-glow">
                  <Search className="h-8 w-8 text-white" aria-hidden="true" strokeWidth={2.5} />
                </div>
                <div className="text-white text-xl font-semibold mb-2">Searching for delicious recipes...</div>
                <div className="text-white/80 text-lg">Please wait while we find the perfect dishes for you</div>
                <div className="mt-4 flex justify-center">
                  <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                </div>
              </div>
            </div>
          )}

          {/* SEARCH ERROR STATE */}
          {!loading && isError && (
            <div className="w-full text-center py-12 animate-fade-in">
              <div className="bg-glass-warm backdrop-blur-lg rounded-3xl p-8 border border-white/30 shadow-glass inline-block max-w-lg">
                <div className="text-white text-2xl font-bold mb-4">Unable to load recipes</div>
                <div className="text-white/90 text-lg mb-6 leading-relaxed">
                  {recipesError?.message || 'Something went wrong while searching. Please try again.'}
                </div>
                <button
                  type="button"
                  onClick={() => queryClient.invalidateQueries(['recipes', user?.id, normalizedQuery, recipeResultLimit])}
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 shadow-modern"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {/* ENHANCED EMPTY STATE */}
          {!loading && !isError && recipes.length === 0 && (
            <div className="w-full text-center py-12 animate-fade-in">
              <div className="bg-glass-warm backdrop-blur-lg rounded-3xl p-8 border border-white/30 shadow-glass inline-block max-w-lg">
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-orange-100 text-4xl shadow-modern" aria-hidden="true">
                  😔
                </div>
                <div className="text-white text-2xl font-bold mb-4">No recipes found</div>
                <div className="text-white/90 text-lg mb-6 leading-relaxed">
                  Don't worry! Try searching for something delicious like <strong>"pasta"</strong>, <strong>"chicken"</strong>, or <strong>"salad"</strong>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {['pasta', 'chicken', 'salad', 'soup', 'dessert'].map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => submitSearchQuery(suggestion)}
                      className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 shadow-modern"
                    >
                      Try {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* RECIPE RESULTS WITH STRATEGIC AD PLACEMENT */}
          {!loading && recipes.length > 0 && recipes.map((recipe, index) => (
            <React.Fragment key={recipe.recipe.label}>
              <div
                className="flex w-full animate-scale-in items-center justify-center sm:w-auto sm:items-stretch"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <Recipe
                  title={recipe.recipe.label}
                  calories={recipe.recipe.calories}
                  image={recipe.recipe.image}
                  ingredients={recipe.recipe.ingredients}
                  url={recipe.recipe.url}
                  isPremium={isPremium}
                  isSaved={savedRecipeUrls.has(recipe.recipe.url)}
                  isSaving={savingRecipeUrls.has(recipe.recipe.url)}
                  onToggleSave={() => handleToggleSavedRecipe(recipe.recipe)}
                />
              </div>

              {/* AD PLACEMENT: Between every 3rd recipe (FREE TIER ONLY) */}
              {(index + 1) % 3 === 0 && index < recipes.length - 1 && userProfile?.tier === 'free' && (
                <div className="w-full flex justify-center">
                  <AdComponent
                    placement="between-recipes"
                    size="large"
                  />
                </div>
              )}
            </React.Fragment>
          ))}
          
          {/* RESULTS COUNT */}
          {!loading && recipes.length > 0 && (
            <div className="w-full text-center pt-4">
              <div className="inline-block max-w-[16.5rem] animate-fade-in rounded-xl border border-white/30 bg-glass-warm p-3 shadow-glass backdrop-blur-lg sm:max-w-none sm:rounded-2xl sm:p-6">
                <div className="mb-0.5 text-sm font-semibold leading-snug text-white sm:mb-1 sm:text-lg">
                  🎉 Found {recipes.length} delicious recipes for "{normalizedQuery}"
                </div>
                <div className="text-xs leading-snug text-white/80 sm:text-sm">
                  Explore these amazing dishes and find your next favorite meal
                </div>
              </div>

              {/* AD PLACEMENT: Bottom of results (FREE TIER ONLY) */}
              {userProfile?.tier === 'free' && (
                <div className="mt-6">
                  <AdComponent
                    placement="bottom-results"
                    size="responsive"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default MainApp
